import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Stack } from "@mui/material";
import PeopleInput from "./PeopleInput/index";
import SessionSelection from "./SessionSelection/index";
import MyCalendar from "../../components/Calendar/index";
import "./BookingPage.css";
import {
  setDate,
  setSession,
  setPeople,
  setPickupPoint,
} from "./carAvailability/carAvailabilitySlice";
import BookingForm from "./booking/BookingForm/index";
import { fetchBlackoutDates } from "../dashboard/ManageBookings/Blackout/BlackoutSlice"; // Adjust the path as necessary
import { verifyAvailability } from "./verifyAvailability/verifyAvailabilityThunk";
import { resetCarAvailabilityState } from "./carAvailability/carAvailabilitySlice";
import { resetBookingState } from "./booking/bookingsSlice";
import PickupPointSelect from "./booking/PickupPoint";

function BookingPage() {
  const dispatch = useDispatch();
  const people = useSelector((state) => state.carAvailability.people);
  const pickupPoint = useSelector((state) => state.carAvailability.pickupPoint); // Get the pickup point from Redux

  const fetchWithRetry = useCallback(
    async (attempt = 1) => {
      try {
        await dispatch(fetchBlackoutDates());
      } catch (error) {
        if (attempt < 3) {
          console.error(`Attempt ${attempt} failed. Retrying...`);
          fetchWithRetry(attempt + 1);
        } else {
          console.error("Failed to fetch after 3 attempts.", error);
        }
      }
    },
    [dispatch]
  );
  useEffect(() => {
    // Reset the state when the component mounts
    dispatch(resetCarAvailabilityState());
    dispatch(resetBookingState());
    fetchWithRetry();
  }, [dispatch, fetchWithRetry]);

  const [availabilitySuccess, setAvailabilitySuccess] = useState(false);
  const [inputsDisabled, setInputsDisabled] = useState(false); // New state

  const handleDateChange = (date) => {
    dispatch(setDate(date));
  };

  const handleSessionChange = (session) => {
    dispatch(setSession(session));
  };

  const handlePeopleChange = (people) => {
    dispatch(setPeople(people));
  };

  const handlePickupPointChange = (point) => {
    dispatch(setPickupPoint(point)); // Dispatch the pickup point to Redux
  };

  const handleBooking = () => {
    if (!people || people <= 0) {
      alert("Please enter the number of people.");
      return;
    }

    if (!pickupPoint) {
      alert("Please select a pickup point.");
      return;
    }

    dispatch(verifyAvailability())
      .unwrap()
      .then((result) => {
        if (result.status === "available") {
          setAvailabilitySuccess(true);
          setInputsDisabled(true);
        }
      })
      .catch((error) => {
        console.error(error);
        alert(error.message); // This will inform the user which session(s) don't have enough space
      });
  };

  const blackoutDates = useSelector((state) => state.blackoutDates);

  return (
    <div className="booking-page">
      <div className="booking-shell">
        <header className="booking-hero">
          <div className="booking-hero__content">
            <div className="booking-hero__branding">
            <img src="/yavola_logo.png" alt="Yavola logo" className="booking-logo" />
              <span className="booking-eyebrow">Yavola Expeditions</span>
            </div>
            <h1>Craft Your Ideal Adventure Day</h1>
            <p>
              Lock in your preferred date, session, and pickup point. We’ll
              ensure every detail is coordinated so you can arrive ready to
              explore.
            </p>
            <div className="booking-hero__meta">
              <span className="booking-pill">Guided small-group tours</span>
              <span className="booking-pill">Flexible pickup network</span>
              <span className="booking-pill">Instant availability check</span>
            </div>
          </div>
          <div className="booking-hero__card">
            <div className="booking-scorecard">
              <h2>Your trip at a glance</h2>
              <ul className="booking-scorecard__stats">
                <li>
                  <strong>8+</strong>
                  <span>Daily departure windows to match your vibe.</span>
                </li>
                <li>
                  <strong>20+</strong>
                  <span>Pickup spots across the island with live capacity.</span>
                </li>
                <li>
                  <strong>24/7</strong>
                  <span>Support from our on-the-ground concierge team.</span>
                </li>
              </ul>
            </div>
          </div>
        </header>

        <div className="booking-layout">
          <section className="booking-panel booking-panel--primary">
            <div className="booking-section">
              <div className="booking-section__header">
                <span className="booking-section__eyebrow">Step 1</span>
                <h2>Select your date</h2>
                <p>Choose an available day to kick off your journey.</p>
              </div>
              <div className="booking-section__body">
                <MyCalendar
                  onDateChange={handleDateChange}
                  blackoutDates={blackoutDates}
                />
              </div>
            </div>

            <div className="booking-section">
              <div className="booking-section__header">
                <span className="booking-section__eyebrow">Step 2</span>
                <h2>Build your group experience</h2>
                <p>Match a session, headcount, and pickup that suits everyone.</p>
              </div>
              <div className="booking-section__body booking-section__body--form">
                <div className="booking-guidance">
                  <h3>Pro tips</h3>
                  <ul>
                    <li>
                      Sessions fill up fastest a week in advance—secure your
                      preferred time as soon as you can.
                    </li>
                    <li>
                      Each pickup hub offers shaded waiting areas and restrooms;
                      arrive 15 minutes before departure.
                    </li>
                    <li>
                      Travelling with special requirements? Add notes during
                      confirmation and our concierge will follow up.
                    </li>
                  </ul>
                </div>
                <Stack spacing={2.5} className="booking-form-stack">
                  <SessionSelection
                    onSessionChange={handleSessionChange}
                    disabled={inputsDisabled}
                  />
                  <PeopleInput
                    onPeopleChange={handlePeopleChange}
                    disabled={inputsDisabled}
                  />
                  <PickupPointSelect
                    onPickupPointChange={handlePickupPointChange}
                    disabled={inputsDisabled}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleBooking}
                    className="booking-action"
                    sx={{
                      borderRadius: "999px",
                      boxShadow: "0 14px 25px rgba(0, 0, 0, 0.18)",
                      textTransform: "none",
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      paddingY: 1.25,
                    }}
                  >
                    Check availability
                  </Button>
                </Stack>
              </div>
            </div>

            {availabilitySuccess ? (
              <div className="booking-section booking-section--confirmation">
                <div className="booking-section__header">
                  <span className="booking-section__eyebrow">Step 3</span>
                  <h2>Confirm the details</h2>
                  <p>
                    Great choice! Complete the form so we can send your
                    confirmation and concierge briefing.
                  </p>
                </div>
                <div className="booking-section__body booking-section__body--confirmation">
                  <BookingForm pickupPoint={pickupPoint} />
                </div>
              </div>
            ) : (
              <div className="booking-section booking-section--placeholder">
                <div className="booking-section__header">
                  <span className="booking-section__eyebrow">Step 3</span>
                  <h2>Confirm the details</h2>
                  <p>Check availability to reveal your confirmation form.</p>
                </div>
                <div className="booking-section__body booking-section__body--placeholder">
                  <h3>Ready when you are</h3>
                  <p>
                    Once you’ve checked availability, we’ll surface your
                    reservation details right here.
                  </p>
                </div>
              </div>
            )}
          </section>

          <aside className="booking-panel booking-panel--secondary">
            <div className="booking-secondary-card">
              <h3>What’s included</h3>
              <ul className="booking-highlights">
                <li>Certified local guides with insider knowledge.</li>
                <li>Comfortable transfers from your chosen pickup point.</li>
                <li>Complimentary refreshments and tailored itinerary tips.</li>
              </ul>
            </div>

            <div className="booking-secondary-card">
              <h3>Need support?</h3>
              <p>
                Chat with our concierge team anytime at
                <a href="mailto:hello@yavola.com"> hello@yavola.com</a> or call
                us at <a href="tel:+13235550123">+1 (323) 555-0123</a>.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
export default BookingPage;
